﻿import { faCheckCircle, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Link, Skeleton} from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useEffect, useRef, useState } from "react";
import useIsAdminSection from "@/hooks/useIsAdminSection";
import * as Dtos from '../api/dtos';
import useBoardStatus from "@/hooks/useBoardStatus";
import useDebouncedValue from "@/hooks/useDebouncedValue";

interface IUseBoardStatus {
    speakerSection?: boolean;
}

const BoardCheck: FC<IUseBoardStatus> = ({
    speakerSection
}) => {
    const {state: {value: firstName}} = useScopedField<string>("firstName", {value: true});
    const {state: {value: lastName}} = useScopedField<string>("lastName", {value: true});

    const {state: {value: registrationAffiliation}, context: {setValue: setRegistrationAffiliation}} = useScopedField<Dtos.RegistrationAffiliation>("registrationAffiliation", {value: true});

    const {state: {value: alreadyRegistered}} = useScopedField<boolean>("alreadyRegistered", {value: true});

    const debouncedFirstName = useDebouncedValue(firstName);
    const debouncedLastName = useDebouncedValue(lastName);
    const { isBoard, loading } = useBoardStatus(debouncedFirstName ?? undefined, debouncedLastName ?? undefined);

    const isAdminSection = useIsAdminSection();

    const [deferredLoading, setDeferredLoading] = useState(false);
    const timerRef = useRef<number>();

    useEffect(() => {
        if (loading) {
            timerRef.current = window.setTimeout(() => {
                setDeferredLoading(true);
            }, 250);
        }
        else {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
                timerRef.current = undefined;
            }

            setDeferredLoading(false);
        }
    }, [loading, setDeferredLoading, timerRef])

    useEffect(() => {
        if (isBoard) {
            setRegistrationAffiliation(Dtos.RegistrationAffiliation.Board);
        } else if (isBoard === false && registrationAffiliation === Dtos.RegistrationAffiliation.Board) {
            setRegistrationAffiliation(undefined);
        }
    }, [isBoard, registrationAffiliation, setRegistrationAffiliation])

    if (deferredLoading && loading) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                severity="info"
                sx={{
                    alignItems: 'center'
                }}
            >
                <AlertTitle>Board Member Status &ndash; <Skeleton width={120} sx={{display: 'inline-block'}} /></AlertTitle>
                Checking Board Member status...
            </Alert>
        )
    }

    if (alreadyRegistered && !isAdminSection) {
        return null;
    }

    if (firstName && lastName) {
        return (
            <>
            {
                !isBoard ? 
                    isAdminSection ?
                        <Alert
                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                            severity="info"
                            sx={{
                                alignItems: 'center'
                            }}
                        >
                            <AlertTitle>Board Member Status &ndash; Not-Board Member</AlertTitle>
                                Not a Board Member
                        </Alert>

                    :
                        <Alert
                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                            severity="info"
                            sx={{
                                alignItems: 'center'
                            }}
                        >
                            <AlertTitle>Board Member Status &ndash; Not-Board Member</AlertTitle>
                            
                            {
                                speakerSection ?
                                    <>
                                        You are not a Board Member.
                                    </>
                                :
                                    <>
                                        You are not a Board Member. If this is incorrect, please contact BCT on <Link href="tel:0249255255">02 4925 5255</Link>.
                                    </>
                            }
                        </Alert>
                :
                    <Alert
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        severity="success"
                        sx={{
                            alignItems: 'center'
                        }}
                    >
                        <AlertTitle>Board Member Status &ndash; Board Member</AlertTitle>
                        You are a Board Member
                    </Alert>
            }
            </>
        )
    }

    return (
        <Alert
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
            severity="info"
            sx={{
                alignItems: 'center'
            }}
        >
                <AlertTitle>Board Member Status &ndash; Unknown</AlertTitle>
                Please enter your first and last name.
        </Alert>
    );
}

export default BoardCheck;